import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function LoanReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [application, setApplication] = useState(null);
  const [onboardingReceipt, setOnboardingReceipt] = useState(null);
  const [activeTab, setActiveTab] = useState('client');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDisburseModal, setShowDisburseModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [disbursementPhone, setDisbursementPhone] = useState('');
  const [disbursementNotes, setDisbursementNotes] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  
  // NEW: Phone change reason states
  const [phoneChangeReason, setPhoneChangeReason] = useState('');
  const [phoneChangeReasonOther, setPhoneChangeReasonOther] = useState('');

  const [verified, setVerified] = useState({
    clientIdentity: false, guarantors: false, collateral: false,
    businessLocation: false, businessPhotos: false, phoneNumber: false,
    loanAmount: false, repaymentTerms: false
  });

  const [disbursementStatus, setDisbursementStatus] = useState(null);
  const [disbursementId, setDisbursementId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState('');
  const [finalizing, setFinalizing] = useState(false);

  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchApplication();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/loans/applications/${id}/review`);
      setApplication(res.data.data.application);
      setOnboardingReceipt(res.data.data.onboardingReceipt);
      setDisbursementPhone(res.data.data.application.client?.phone || '');
      setApprovedAmount(res.data.data.application.requestedAmount || '');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load application');
      navigate('/loans/applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approvedAmount || approvedAmount <= 0) return alert('Enter a valid approved amount');
    setSubmitting(true);
    try {
      await api.patch(`/loans/applications/${id}/approve`, { approvedAmount: parseFloat(approvedAmount), notes: approveNotes });
      alert('Application approved successfully!');
      fetchApplication();
      setShowApproveModal(false);
    } catch (err) { alert(err.response?.data?.message || 'Approval failed'); }
    finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return alert('Please provide a rejection reason');
    setSubmitting(true);
    try {
      await api.patch(`/loans/applications/${id}/reject`, { reason: rejectReason });
      alert('Application rejected');
      navigate('/loans/applications');
    } catch (err) { alert(err.response?.data?.message || 'Rejection failed'); }
    finally { setSubmitting(false); }
  };

  const handleFinalize = async () => {
    if (!confirm('This will create the Loan record and repayment schedule. Interest will start accruing from tomorrow. Continue?')) return;
    setFinalizing(true);
    try {
      const res = await api.post(`/loans/applications/${id}/finalize`);
      alert(`Loan created successfully! Loan #${res.data.data.loanNumber}. Interest will start accruing daily via cron job.`);
      fetchApplication();
    } catch (err) { alert(err.response?.data?.message || 'Failed to create loan'); }
    finally { setFinalizing(false); }
  };

  const handleDisburse = async () => {
    const allVerified = Object.values(verified).every(v => v);
    if (!allVerified) return alert('Please verify all items before disbursing');
    
    let phone = disbursementPhone.replace(/\s/g, '');
    if (phone.startsWith('0')) phone = '254' + phone.substring(1);
    if (phone.startsWith('+')) phone = phone.substring(1);
    if (!/^254\d{9}$/.test(phone)) return alert('Invalid phone number format');
    
    const amt = application.approvedAmount || application.requestedAmount;
    
    // NEW: Validate phone change reason if phone is different
    const isPhoneDifferent = disbursementPhone.replace(/\s/g, '') !== (application.client?.phone || '').replace(/\s/g, '') && disbursementPhone;
    if (isPhoneDifferent) {
      if (!phoneChangeReason) {
        return alert('⚠️ You must provide a reason for using a different phone number');
      }
      if (phoneChangeReason === 'other' && !phoneChangeReasonOther.trim()) {
        return alert('⚠️ Please specify the reason for using a different phone number');
      }
    }
    
    if (!confirm(`Confirm disbursement of KES ${amt?.toLocaleString()} to ${phone}?${isPhoneDifferent ? '\n\n⚠️ NOTE: Sending to a DIFFERENT number than registered!' : ''}`)) return;
    
    setSubmitting(true);
    try {
      // NEW: Build the payload with phone change reason
      const payload = {
        disbursementPhone: phone,
        verifiedDetails: verified,
        verificationNotes: disbursementNotes,
        notes: disbursementNotes
      };
      
      if (isPhoneDifferent) {
        payload.phoneChangeReason = phoneChangeReason === 'other' ? phoneChangeReasonOther : phoneChangeReason;
      }
      
      const res = await api.post(`/loans/applications/${id}/disburse`, payload);
      const { disbursementId: dId, status } = res.data.data;
      setDisbursementId(dId);
      setDisbursementStatus(status);
      setShowDisburseModal(false);
      setPollMessage(`Sending KES ${amt?.toLocaleString()} to ${phone}...`);
      setPolling(true);
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/loans/disbursements/${dId}/status`);
          const data = statusRes.data.data;
          setDisbursementStatus(data.status);
          if (data.status === 'success') {
            clearInterval(pollRef.current); clearTimeout(timeoutRef.current);
            setPolling(false); setPollMessage('');
            fetchApplication();
          } else if (data.status === 'failed') {
            clearInterval(pollRef.current); clearTimeout(timeoutRef.current);
            setPolling(false); setPollMessage('');
            alert(`Disbursement failed: ${data.resultDesc}`);
            fetchApplication();
          }
        } catch (e) {}
      }, 3000);
      timeoutRef.current = setTimeout(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        setPolling(false); setPollMessage('');
        alert('Disbursement check timed out. Check disbursement history for the final status.');
      }, 180000);
    } catch (err) { alert(err.response?.data?.message || 'Disbursement failed'); }
    finally { setSubmitting(false); }
  };

  const formatPhone = (p) => {
    if (!p) return '';
    let f = p.replace(/\s/g, '');
    if (f.startsWith('254')) return '0' + f.substring(3);
    return f;
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: 'badge-secondary', submitted: 'badge-warning', 'under-review': 'badge-warning',
      approved: 'badge-info', disbursing: 'badge-primary', disbursed: 'badge-success',
      'disbursement_failed': 'badge-danger', rejected: 'badge-danger', withdrawn: 'badge-secondary',
      active: 'badge-success', completed: 'badge-secondary', defaulted: 'badge-danger'
    };
    return map[status] || 'badge-secondary';
  };

  // NEW: Reset phone change reason when modal opens/closes
  const openDisburseModal = () => {
    setPhoneChangeReason('');
    setPhoneChangeReasonOther('');
    setShowDisburseModal(true);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block', fontSize: '24px' }}>⏳</span>
        <p className="mt-2 text-muted">Loading application...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (!application) return null;

  const client = application.client || {};
  const product = application.loanProduct || {};
  const branch = application.branch || {};
  const guarantors = application.guarantors || [];
  const collateral = application.collateral || [];
  const photos = application.businessPhotos || [];
  const allVerified = Object.values(verified).every(v => v);

  // NEW: Check if phone is different
  const isPhoneDifferent = disbursementPhone.replace(/\s/g, '') !== (client.phone || '').replace(/\s/g, '') && disbursementPhone;
  
  // NEW: Check if phone change reason is valid
  const isPhoneReasonValid = phoneChangeReason && (phoneChangeReason !== 'other' || phoneChangeReasonOther.trim());

  const isLoanActive = application.status === 'disbursed' && application.convertedToLoan;
  const needsFinalization = application.status === 'disbursed' && !application.convertedToLoan;

  // ═══════════════════════════════════════════════════════════
  // Calculate estimated interest for display
  // ═══════════════════════════════════════════════════════════
  const duration = application.duration || 0;
  const estimatedInterestPercent = Math.min(duration, 30);
  const estimatedInterest = Math.round((application.approvedAmount || application.requestedAmount || 0) * (estimatedInterestPercent / 100));
  const estimatedTotalRepayable = (application.approvedAmount || application.requestedAmount || 0) + estimatedInterest;

  return (
    <div>
      {/* Header */}
      <div className="card mb-2" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)', padding: '20px 24px', color: '#fff' }}>
          <div className="flex justify-between items-center">
            <div>
              <h3 style={{ margin: 0, color: '#fff' }}>Loan Application Review</h3>
              <p className="text-sm" style={{ color: '#93c5fd', margin: '4px 0 0 0' }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.5px', fontSize: '15px' }}>{application.applicationNumber}</span> — {client.firstName} {client.lastName}
              </p>
            </div>
            <div className="text-right">
              <span className={`badge ${getStatusBadge(application.status)}`} style={{ fontSize: '14px', padding: '6px 16px' }}>
                {application.status?.replace(/_/g, ' ').toUpperCase()}
              </span>
              <p className="text-xs mt-1" style={{ color: '#93c5fd' }}>
                Submitted: {application.submittedAt ? new Date(application.submittedAt).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-4" style={{ padding: '16px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div className="text-center">
            <div className="text-xs text-muted">Product</div>
            <div className="font-bold">{product.name || 'N/A'}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted">Requested</div>
            <div className="font-bold text-primary">KES {(application.requestedAmount || 0).toLocaleString()}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted">Approved</div>
            <div className="font-bold" style={{ color: application.approvedAmount ? '#059669' : '#dc3545' }}>
              {application.approvedAmount ? `KES ${application.approvedAmount.toLocaleString()}` : 'NOT APPROVED'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted">Est. Total Repayable</div>
            <div className="font-bold" style={{ color: '#f59e0b' }}>KES {estimatedTotalRepayable.toLocaleString()}</div>
            <div className="text-xs text-muted">{estimatedInterestPercent}% over {duration} days</div>
          </div>
        </div>

        <div className="flex gap-2 p-3" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-outline" onClick={() => navigate('/loans/applications')}>← Back</button>
          <button className="btn btn-outline" onClick={() => navigate('/loans/disbursements')}>💰 Disbursement History</button>

          {['submitted', 'under-review'].includes(application.status) && (
            <>
              <button className="btn btn-danger" onClick={() => setShowRejectModal(true)}>❌ Reject</button>
              <button className="btn btn-info" onClick={() => setShowApproveModal(true)}>✅ Approve</button>
            </>
          )}

          {application.status === 'approved' && (
            <button className="btn btn-success" onClick={openDisburseModal} disabled={polling} style={{ padding: '10px 30px', fontSize: '15px' }}>
              {polling ? '⏳ Processing...' : '💰 Disburse Loan'}
            </button>
          )}

          {isLoanActive ? (
            <button className="btn btn-success" onClick={() => navigate(`/loans/${application.convertedToLoan}`)} style={{ padding: '10px 20px', fontSize: '15px' }}>
              📊 View Active Loan & Schedule
            </button>
          ) : needsFinalization ? (
            <button className="btn btn-warning" onClick={handleFinalize} disabled={finalizing} style={{ padding: '10px 20px', fontSize: '15px' }}>
              {finalizing ? '⏳ Creating Loan...' : '🔧 Finalize & Create Loan'}
            </button>
          ) : application.status === 'disbursing' ? (
            <span className="badge badge-info" style={{ padding: '10px 20px', fontSize: '14px' }}>⏳ Disbursing... (Check History)</span>
          ) : null}
        </div>
      </div>

      {polling && disbursementStatus && (
        <div className="card mb-2" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '2px solid #3b82f6', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
          <span style={{ fontSize: '36px', animation: 'pulse 1.5s ease-in-out infinite', display: 'inline-block' }}>📲</span>
          <h4 style={{ color: '#1e40af', marginTop: 8 }}>Disbursement In Progress</h4>
          <p className="text-sm" style={{ color: '#3b82f6' }}>{pollMessage}</p>
          <p className="text-xs text-muted mt-1">Status: <strong>{disbursementStatus.toUpperCase()}</strong> — Please don't close this page.</p>
          <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }`}</style>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          INTEREST INFO BANNER — shown for approved/disbursed apps
          ═══════════════════════════════════════════════════════════ */}
      {['approved', 'disbursing', 'disbursed'].includes(application.status) && (
        <div className="card mb-2" style={{ borderRadius: 10, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #f59e0b', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📊</span>
            <div>
              <strong style={{ color: '#92400e' }}>Interest Calculation Preview</strong>
              <div style={{ fontSize: 13, color: '#78350f', marginTop: 4 }}>
                <strong>Tiered:</strong> 14% base (Days 1-14), +1%/day (Days 15-30), cap at 30%<br/>
                <strong>For {duration} days:</strong> {estimatedInterestPercent}% = KES {estimatedInterest.toLocaleString()} interest on KES {(application.approvedAmount || application.requestedAmount).toLocaleString()}<br/>
                <strong>Estimated Total Repayable:</strong> KES {estimatedTotalRepayable.toLocaleString()}<br/>
                <strong>Penalties:</strong> 5% after Day 30 (first 15 overdue days), 15% after Day 45
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-2" style={{ borderBottom: '2px solid #e2e8f0' }}>
        {[
          { key: 'client', label: '👤 Client' },
          { key: 'loan', label: '📋 Loan Details' },
          { key: 'guarantors', label: `🤝 Guarantors (${guarantors.filter(g => g.name).length})` },
          { key: 'collateral', label: `🏠 Collateral (${collateral.filter(c => c.type).length})` },
          { key: 'verification', label: '📍 Verification' },
          { key: 'history', label: '📜 History' }
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '12px 20px', border: 'none', background: 'none',
            borderBottom: activeTab === tab.key ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeTab === tab.key ? '#1e40af' : '#64748b',
            fontWeight: activeTab === tab.key ? 600 : 400, fontSize: '14px', cursor: 'pointer'
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* CLIENT TAB */}
      {activeTab === 'client' && (
        <div className="card p-3">
          <h4 className="mb-2">Client Information</h4>
          <div className="grid grid-3 gap-2">
            {[
              { label: 'Full Name', value: `${client.firstName} ${client.lastName}` },
              { label: 'National ID', value: client.clientNumber },
              { label: 'Phone Number', value: formatPhone(client.phone) },
              { label: 'Email', value: client.email || 'N/A' },
              { label: 'Occupation', value: client.occupation || 'N/A' },
              { label: 'Monthly Income', value: client.monthlyIncome ? `KES ${client.monthlyIncome.toLocaleString()}` : 'N/A' },
              { label: 'Business Name', value: client.businessName || 'N/A' },
              { label: 'Business Type', value: client.businessType || 'N/A' },
              { label: 'Branch', value: branch.name || 'N/A' }
            ].map((item, i) => (
              <div key={i} className="card p-2" style={{ background: '#f8fafc' }}>
                <div className="text-xs text-muted">{item.label}</div>
                <div className="font-bold">{item.value}</div>
              </div>
            ))}
          </div>
          {onboardingReceipt && (
            <div className="card mt-2 p-2" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '1px solid #10b981', borderRadius: '8px' }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-xs" style={{ color: '#047857' }}>Onboarding Fee Receipt</div>
                  <div className="font-bold" style={{ fontSize: '18px', fontFamily: 'monospace', color: '#065f46', letterSpacing: '1px' }}>{onboardingReceipt}</div>
                </div>
                <span style={{ fontSize: '28px' }}>✅</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* LOAN DETAILS TAB */}
      {activeTab === 'loan' && (
        <div className="card p-3">
          <h4 className="mb-2">Loan Details</h4>
          <div className="grid grid-2 gap-2">
            <div className="card p-2" style={{ background: '#f8fafc' }}>
              <div className="text-xs text-muted">Loan Product</div>
              <div className="font-bold">{product.name || 'N/A'}</div>
              <div className="text-xs text-muted">Interest: <strong>{product.interestRate || 'N/A'}%</strong> (Base rate, tiered calculation applies)</div>
            </div>
            <div className="card p-2" style={{ background: '#f8fafc' }}>
              <div className="text-xs text-muted">Purpose</div>
              <div className="font-bold">{application.purpose || 'N/A'}</div>
            </div>
            <div className="card p-2" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <div className="text-xs text-muted">Requested Amount</div>
              <div className="font-bold text-primary" style={{ fontSize: '20px' }}>KES {(application.requestedAmount || 0).toLocaleString()}</div>
            </div>
            <div className="card p-2" style={{ background: application.approvedAmount ? '#ecfdf5' : '#fef2f2', border: `1px solid ${application.approvedAmount ? '#10b981' : '#fecaca'}` }}>
              <div className="text-xs text-muted">Approved Amount</div>
              <div className="font-bold" style={{ fontSize: '20px', color: application.approvedAmount ? '#059669' : '#dc3545' }}>
                {application.approvedAmount ? `KES ${application.approvedAmount.toLocaleString()}` : 'NOT YET APPROVED'}
              </div>
            </div>
            <div className="card p-2" style={{ background: '#f8fafc' }}>
              <div className="text-xs text-muted">Duration</div>
              <div className="font-bold">{application.duration} {application.duration === 1 ? 'Day' : 'Days'}</div>
            </div>
            <div className="card p-2" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
              <div className="text-xs text-muted">Estimated Interest ({estimatedInterestPercent}%)</div>
              <div className="font-bold" style={{ fontSize: '20px', color: '#92400e' }}>KES {estimatedInterest.toLocaleString()}</div>
              <div className="text-xs text-muted">Est. Total: KES {estimatedTotalRepayable.toLocaleString()}</div>
            </div>
          </div>

          {application.approvalNotes && (
            <div className="card mt-2 p-2" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
              <div className="text-xs text-muted">Approval Notes</div>
              <div>{application.approvalNotes}</div>
            </div>
          )}
          {application.rejectionReason && (
            <div className="card mt-2 p-2" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div className="text-xs text-muted">Rejection Reason</div>
              <div className="text-danger">{application.rejectionReason}</div>
            </div>
          )}

          {application.status === 'disbursed' && (
            <div className="card mt-2 p-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '2px solid #10b981', borderRadius: '10px' }}>
              <h4 style={{ color: '#065f46', marginTop: 0 }}>✅ Disbursement Completed</h4>
              <div className="grid grid-2 gap-2 mt-2">
                <div><div className="text-xs text-muted">Amount Sent</div><div className="font-bold" style={{ fontSize: '18px', color: '#065f46' }}>KES {(application.disbursementAmount || 0).toLocaleString()}</div></div>
                <div><div className="text-xs text-muted">Sent To</div><div className="font-bold">{formatPhone(application.disbursementPhone)}</div></div>
                <div><div className="text-xs text-muted">M-Pesa Receipt</div><div className="font-bold" style={{ fontFamily: 'monospace', fontSize: '16px', color: '#065f46' }}>{application.disbursementReceipt || 'N/A'}</div></div>
                <div><div className="text-xs text-muted">Disbursed At</div><div className="font-bold">{application.disbursedAt ? new Date(application.disbursedAt).toLocaleString() : 'N/A'}</div></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* GUARANTORS TAB */}
      {activeTab === 'guarantors' && (
        <div className="card p-3">
          <h4 className="mb-2">Guarantors / Referees ({guarantors.filter(g => g.name).length})</h4>
          {guarantors.length === 0 && <p className="text-muted">No guarantors listed</p>}
          {guarantors.map((g, i) => (
            <div key={i} className="card p-2 mb-1" style={{ background: '#f8fafc', borderLeft: g.name ? '4px solid #10b981' : '4px solid #e2e8f0' }}>
              <div className="flex justify-between items-center">
                <strong>Guarantor {i + 1}</strong>
                {g.name ? <span className="badge badge-success" style={{ fontSize: '10px' }}>COMPLETE</span> : <span className="badge badge-secondary" style={{ fontSize: '10px' }}>EMPTY</span>}
              </div>
              <div className="grid grid-2 gap-1 mt-1">
                <div><span className="text-xs text-muted">Name:</span> <strong>{g.name || '—'}</strong></div>
                <div><span className="text-xs text-muted">Phone:</span> <strong>{g.phone || '—'}</strong></div>
                <div><span className="text-xs text-muted">ID Number:</span> <strong>{g.nationalId || '—'}</strong></div>
                <div><span className="text-xs text-muted">Relationship:</span> <strong>{g.relationship || '—'}</strong></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* COLLATERAL TAB */}
      {activeTab === 'collateral' && (
        <div className="card p-3">
          <h4 className="mb-2">Collateral ({collateral.filter(c => c.type).length})</h4>
          {collateral.length === 0 && <p className="text-muted">No collateral listed</p>}
          {collateral.map((c, i) => (
            <div key={i} className="card p-2 mb-1" style={{ background: '#f8fafc', borderLeft: c.type ? '4px solid #10b981' : '4px solid #e2e8f0' }}>
              <div className="flex justify-between items-center mb-1">
                <strong>Item {i + 1}</strong>
                {c.type ? <span className="badge badge-success" style={{ fontSize: '10px' }}>COMPLETE</span> : <span className="badge badge-secondary" style={{ fontSize: '10px' }}>EMPTY</span>}
              </div>
              <div className="grid grid-3 gap-1">
                <div><span className="text-xs text-muted">Type:</span> <strong>{c.type || '—'}</strong></div>
                <div><span className="text-xs text-muted">Description:</span> <strong>{c.description || '—'}</strong></div>
                <div><span className="text-xs text-muted">Value:</span> <strong>{c.estimatedValue ? `KES ${parseFloat(c.estimatedValue).toLocaleString()}` : '—'}</strong></div>
              </div>
              {c.image && <div className="mt-1"><img src={c.image} alt={`Collateral ${i + 1}`} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} /></div>}
            </div>
          ))}
        </div>
      )}

      {/* VERIFICATION TAB */}
      {activeTab === 'verification' && (
        <div className="card p-3">
          <h4 className="mb-2">Business Verification</h4>
          <div className="card p-2 mb-2" style={{ background: application.businessLocation ? '#ecfdf5' : '#fef2f2', border: `1px solid ${application.businessLocation ? '#10b981' : '#fecaca'}` }}>
            <div className="flex justify-between items-center">
              <div>
                <strong>GPS Location</strong>
                {application.businessLocation ? (
                  <div className="text-sm mt-1">📍 Lat: {application.businessLocation.latitude?.toFixed(6)} | Lng: {application.businessLocation.longitude?.toFixed(6)}</div>
                ) : (
                  <div className="text-sm text-danger mt-1">❌ No GPS location captured</div>
                )}
              </div>
              {application.businessLocation ? <span style={{ fontSize: '24px' }}>✅</span> : <span style={{ fontSize: '24px' }}>❌</span>}
            </div>
          </div>
          <div className="mb-2">
            <strong>Business Photos ({photos.length})</strong>
            {photos.length > 0 ? (
              <div className="grid grid-4 gap-1 mt-1">
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={p.image || p} alt={`Business ${i + 1}`} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    {p.description && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', fontSize: '11px', borderRadius: '0 0 8px 8px' }}>{p.description}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-danger text-sm mt-1">❌ No business photos uploaded</div>
            )}
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="card p-3">
          <h4 className="mb-2">Application History</h4>
          <div style={{ position: 'relative', paddingLeft: '20px' }}>
            <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: '2px', background: '#e2e8f0' }}></div>
            {[
              { label: 'Application Created', time: application.createdAt, user: 'System' },
              { label: 'Application Submitted', time: application.submittedAt, user: application.submittedBy?.name },
              { label: 'Under Review', time: application.reviewedAt, user: application.reviewedBy?.name },
              ...(application.approvedAt ? [{ label: 'Approved', time: application.approvedAt, user: application.approvedBy?.name, extra: `Amount: KES ${application.approvedAmount?.toLocaleString()}` }] : []),
              ...(application.rejectedAt ? [{ label: 'Rejected', time: application.rejectedAt, user: 'N/A', extra: application.rejectionReason }] : []),
              ...(application.disbursedAt ? [{ label: 'Disbursed', time: application.disbursedAt, user: 'System', extra: `Receipt: ${application.disbursementReceipt}` }] : []),
              ...(application.status === 'disbursement_failed' ? [{ label: 'Disbursement Failed', time: new Date(), user: 'System', extra: 'Check disbursement history for details' }] : [])
            ].filter(item => item.time).map((item, i) => (
              <div key={i} style={{ position: 'relative', marginBottom: '16px' }}>
                <div style={{ position: 'absolute', left: '-18px', top: 4, width: '10px', height: '10px', borderRadius: '50%', background: item.time ? '#10b981' : '#e2e8f0', border: '2px solid #fff' }}></div>
                <div>
                  <strong className="text-sm">{item.label}</strong>
                  <div className="text-xs text-muted">{new Date(item.time).toLocaleString()}</div>
                  {item.user && <div className="text-xs text-muted">By: {item.user}</div>}
                  {item.extra && <div className="text-xs" style={{ color: '#047857' }}>{item.extra}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* APPROVE MODAL */}
      {showApproveModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card p-3" style={{ width: '500px', maxHeight: '90vh', overflow: 'auto' }}>
            <h4>✅ Approve Application</h4>
            <p className="text-sm text-muted mb-2">Approving loan for <strong>{client.firstName} {client.lastName}</strong></p>
            <div className="form-group">
              <label>Approved Amount (KES)*</label>
              <input type="number" className="form-control" value={approvedAmount} onChange={e => setApprovedAmount(e.target.value)} max={product.maxAmount} />
              {product.maxAmount && <div className="text-xs text-muted">Max for this product: KES {product.maxAmount.toLocaleString()}</div>}
            </div>
            <div className="card p-2 mb-2" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8 }}>
              <div className="text-xs font-bold" style={{ color: '#92400e' }}>📊 Estimated Interest Preview</div>
              <div className="text-sm" style={{ color: '#78350f', marginTop: 4 }}>
                {duration} days × tiered rate = {estimatedInterestPercent}%<br/>
                Interest: KES {Math.round((parseFloat(approvedAmount) || 0) * (estimatedInterestPercent / 100)).toLocaleString()}<br/>
                <strong>Total Repayable: KES {((parseFloat(approvedAmount) || 0) + Math.round((parseFloat(approvedAmount) || 0) * (estimatedInterestPercent / 100))).toLocaleString()}</strong>
              </div>
            </div>
            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea className="form-control" rows={3} value={approveNotes} onChange={e => setApproveNotes(e.target.value)} placeholder="Approval notes..."></textarea>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-outline flex-1" onClick={() => setShowApproveModal(false)} disabled={submitting}>Cancel</button>
              <button className="btn btn-info flex-1" onClick={handleApprove} disabled={submitting}>{submitting ? '⏳...' : '✅ Approve'}</button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card p-3" style={{ width: '450px' }}>
            <h4 style={{ color: '#dc3545' }}>❌ Reject Application</h4>
            <p className="text-sm text-muted mb-2">Rejecting loan for <strong>{client.firstName} {client.lastName}</strong></p>
            <div className="form-group">
              <label>Rejection Reason*</label>
              <textarea className="form-control" rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Provide clear reason..." required></textarea>
            </div>
            <div className="flex gap-2">
              <button className="btn btn-outline flex-1" onClick={() => setShowRejectModal(false)} disabled={submitting}>Cancel</button>
              <button className="btn btn-danger flex-1" onClick={handleReject} disabled={submitting || !rejectReason.trim()}>{submitting ? '⏳...' : '❌ Reject'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DISBURSE MODAL */}
      {showDisburseModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card p-3" style={{ width: '540px', maxHeight: '90vh', overflow: 'auto' }}>
            <h4 style={{ color: '#059669' }}>💰 Disburse Loan</h4>
            <p className="text-sm text-muted mb-2">
              Disbursing <strong>KES {(application.approvedAmount || application.requestedAmount)?.toLocaleString()}</strong> to <strong>{client.firstName} {client.lastName}</strong>
            </p>

            {/* Verification Checklist */}
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: allVerified ? '2px solid #10b981' : '2px solid #f59e0b' }}>
              <div className="text-sm font-bold mb-2">{allVerified ? '✅ All Verified' : '⚠️ Verification Checklist (all required)'}</div>
              {[
                { key: 'clientIdentity', label: 'Client identity verified against ID' },
                { key: 'guarantors', label: `All guarantors verified (${guarantors.filter(g => g.name).length} listed)` },
                { key: 'collateral', label: `Collateral verified (${collateral.filter(c => c.type).length} items)` },
                { key: 'businessLocation', label: 'GPS location captured' },
                { key: 'businessPhotos', label: `Business photos reviewed (${photos.length} photos)` },
                { key: 'phoneNumber', label: 'Disbursement phone number confirmed with client' },
                { key: 'loanAmount', label: `Approved amount confirmed: KES ${(application.approvedAmount || 0).toLocaleString()}` },
                { key: 'repaymentTerms', label: 'Repayment terms explained to client' }
              ].map(item => (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: '13px' }}>
                  <input type="checkbox" checked={verified[item.key]} onChange={e => setVerified(prev => ({ ...prev, [item.key]: e.target.checked }))} style={{ width: 18, height: 18, accentColor: '#059669' }} />
                  <span style={{ color: verified[item.key] ? '#059669' : '#64748b' }}>{verified[item.key] ? '✅' : '⬜'} {item.label}</span>
                </label>
              ))}
            </div>

            {/* Phone Number Input */}
            <div className="form-group">
              <label style={{ fontWeight: 600 }}>Disbursement Phone Number*</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '20px' }}>🇰🇪</span>
                <input 
                  type="tel" 
                  className="form-control" 
                  value={disbursementPhone} 
                  onChange={e => { 
                    setDisbursementPhone(e.target.value); 
                    setVerified(prev => ({ ...prev, phoneNumber: false }));
                    // NEW: Reset reason when phone changes
                    setPhoneChangeReason('');
                    setPhoneChangeReasonOther('');
                  }} 
                  placeholder="0712 345 678" 
                  style={{ 
                    fontSize: '16px', 
                    letterSpacing: '1px', 
                    padding: '10px 14px',
                    border: isPhoneDifferent ? '2px solid #ef4444' : '1px solid #dee2e6'
                  }} 
                />
              </div>
              <div className="text-xs text-muted mt-1">
                Registered phone: <strong>{formatPhone(client.phone)}</strong>
              </div>
            </div>

            {/* NEW: Phone Change Reason - Only shows when phone is different */}
            {isPhoneDifferent && (
              <div style={{ 
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', 
                border: '2px solid #fca5a5', 
                borderRadius: '10px', 
                padding: '16px', 
                marginBottom: '16px',
                animation: 'shake 0.5s ease-in-out'
              }}>
                <style>{`
                  @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    20% { transform: translateX(-5px); }
                    40% { transform: translateX(5px); }
                    60% { transform: translateX(-3px); }
                    80% { transform: translateX(3px); }
                  }
                `}</style>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '12px' }}>
                  <span style={{ fontSize: '22px' }}>🚨</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '14px' }}>
                      Different Phone Number Detected!
                    </div>
                    <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: 2 }}>
                      Registered: <strong>{formatPhone(client.phone)}</strong> → Entering: <strong>{disbursementPhone}</strong>
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label style={{ fontWeight: 600, color: '#991b1b', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                    Why is this phone different from the registered number?*
                  </label>
                  <select 
                    className="form-control" 
                    value={phoneChangeReason}
                    onChange={e => setPhoneChangeReason(e.target.value)}
                    style={{ fontSize: '13px', border: '1px solid #fca5a5' }}
                  >
                    <option value="">-- Select a reason --</option>
                    <option value="client_requested">Client explicitly requested this number for disbursement</option>
                    <option value="wrong_number_registered">Wrong number was registered during onboarding</option>
                    <option value="phone_lost">Client lost the registered phone/SIM card</option>
                    <option value="different_mpesa">Client uses a different M-Pesa account for loans</option>
                    <option value="other">Other reason (specify below)</option>
                  </select>
                </div>

                {phoneChangeReason === 'other' && (
                  <div className="form-group">
                    <input 
                      className="form-control" 
                      placeholder="Please specify the reason..."
                      value={phoneChangeReasonOther}
                      onChange={e => setPhoneChangeReasonOther(e.target.value)}
                      style={{ fontSize: '13px', border: '1px solid #fca5a5' }}
                    />
                  </div>
                )}

                <div style={{ 
                  background: '#fff', 
                  border: '1px solid #fecaca', 
                  borderRadius: '6px', 
                  padding: '8px 10px', 
                  fontSize: '11px', 
                  color: '#991b1b' 
                }}>
                  <strong>📝 Audit Notice:</strong> This reason will be recorded in the disbursement record for compliance purposes.
                </div>
              </div>
            )}

            {/* Amount Display */}
            <div className="card p-2 mb-2" style={{ 
              background: isPhoneDifferent ? '#fffbeb' : '#ecfdf5', 
              border: `1px solid ${isPhoneDifferent ? '#fcd34d' : '#10b981'}`, 
              textAlign: 'center', 
              borderRadius: '8px' 
            }}>
              <div className="text-xs text-muted">Amount to Disburse</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#065f46', margin: '4px 0' }}>
                KES {(application.approvedAmount || application.requestedAmount)?.toLocaleString()}
              </div>
              {isPhoneDifferent && (
                <div style={{ fontSize: '11px', color: '#92400e', fontWeight: 600 }}>
                  ⚠️ Will be sent to: {disbursementPhone}
                </div>
              )}
            </div>

            {/* Interest Warning */}
            <div className="card p-2 mb-2" style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12 }}>
              <strong style={{ color: '#92400e' }}>⚠️ Interest starts accruing the day after disbursement:</strong>
              <div style={{ color: '#78350f', marginTop: 4 }}>
                • Days 1-14: 14% flat rate<br/>
                • Days 15-30: +1% per additional day (capped at 30%)<br/>
                • After Day 30: Penalties apply (5% first 15 days, then 15%)
              </div>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes (Optional)</label>
              <textarea className="form-control" rows={2} value={disbursementNotes} onChange={e => setDisbursementNotes(e.target.value)} placeholder="Disbursement notes..."></textarea>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button className="btn btn-outline flex-1" onClick={() => setShowDisburseModal(false)} disabled={submitting}>Cancel</button>
              <button 
                className="btn btn-success flex-1" 
                onClick={handleDisburse} 
                disabled={
                  submitting || 
                  !allVerified || 
                  !disbursementPhone ||
                  (isPhoneDifferent && !isPhoneReasonValid)
                } 
                style={{ 
                  opacity: (submitting || !allVerified || !disbursementPhone || (isPhoneDifferent && !isPhoneReasonValid)) ? 0.5 : 1,
                  background: isPhoneDifferent ? '#f59e0b' : undefined,
                  borderColor: isPhoneDifferent ? '#f59e0b' : undefined
                }}
              >
                {submitting ? '⏳ Initiating...' : isPhoneDifferent ? '⚠️ Confirm to Different Number' : '💰 Confirm Disbursement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}